import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp, CheckCircle2, ArrowRight } from 'lucide-react';
import { IMPROVEMENT_TEMPLATES, DimensionKey } from '@/lib/rubricParser';
import { 
  aggregateDimensionsToCategories, 
  CategoryScore, 
  DOING_WELL_TEMPLATES,
  BEGINNER_CATEGORIES,
  getDimensionLabel,
  getDimensionMax 
} from '@/lib/rubricCategories';

// Rubric dimensions with their max values and display labels
const DIMENSIONS = [
  { key: 'outcome_clarity', label: 'Outcome clarity', max: 15 },
  { key: 'role_assignment', label: 'Role assignment', max: 6 },
  { key: 'context_background', label: 'Context + background', max: 12 },
  { key: 'constraints_scope', label: 'Constraints + scope', max: 15 },
  { key: 'inputs_sources', label: 'Inputs + sources', max: 10 },
  { key: 'output_format', label: 'Output format', max: 10 },
  { key: 'quality_bar', label: 'Quality bar', max: 10 },
  { key: 'method_process', label: 'Method / process', max: 8 },
  { key: 'risk_controls', label: 'Risk controls', max: 7 },
  { key: 'iteration_hygiene', label: 'Iteration hygiene', max: 7 },
];

interface FluencyStrengthsProps {
  dimensionAvgs: Record<string, number> | null;
  scoredCount?: number;
}

interface RankedDimension {
  key: string;
  label: string;
  max: number;
  avgRaw: number;
  percentOfMax: number;
}

// Category bar for beginner view
function CategoryBar({ 
  category,
}: { 
  category: CategoryScore;
}) {
  const percentage = category.percentOfMax * 100;
  const isExcellent = percentage >= 90;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-base">
        <span className="font-medium">{category.label}</span>
        <span className="font-mono text-muted-foreground">
          {category.score.toFixed(1)} <span className="text-muted-foreground/60">/ {category.max}</span>
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-300 ${
            percentage >= 60 ? 'bg-success' : 'bg-primary/80'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      {/* Doing well / Improve feedback */}
      <div className="mt-1.5 space-y-0.5">
        <div className="flex items-start gap-1.5">
          <CheckCircle2 className="h-3 w-3 text-success shrink-0 mt-0.5" />
          <span className="text-base text-muted-foreground leading-snug">
            {category.doingWell}
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <ArrowRight className={`h-3 w-3 shrink-0 mt-0.5 ${isExcellent ? 'text-success' : 'text-warning'}`} />
          <span className={`text-base leading-snug ${isExcellent ? 'text-success' : 'text-muted-foreground'}`}>
            {category.improve}
          </span>
        </div>
      </div>
    </div>
  );
}

// Dimension bar for advanced view
function DimensionBar({ 
  label, 
  avgRaw, 
  max, 
  percentOfMax, 
  isStrength,
  dimensionKey,
  showExplanation = false,
}: { 
  label: string; 
  avgRaw: number; 
  max: number; 
  percentOfMax: number; 
  isStrength?: boolean;
  dimensionKey?: DimensionKey;
  showExplanation?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-base">
        <span className="flex items-center gap-1.5">
          {isStrength !== undefined && (
            isStrength ? (
              <TrendingUp className="h-3.5 w-3.5 text-success" />
            ) : (
              <TrendingDown className="h-3.5 w-3.5 text-muted-foreground" />
            )
          )}
          {label}
        </span>
        <span className="font-mono text-muted-foreground">
          {avgRaw.toFixed(1)} <span className="text-muted-foreground/60">/ {max}</span>
        </span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-300 ${
            isStrength ? 'bg-success' : 'bg-primary/70'
          }`}
          style={{ width: `${percentOfMax * 100}%` }}
        />
      </div>
      {showExplanation && dimensionKey && (
        <div className="mt-1.5 pl-5 space-y-0.5 text-xs text-muted-foreground">
          <p><span className="text-foreground/70">Doing well:</span> {DOING_WELL_TEMPLATES[dimensionKey]}</p>
          <p><span className="text-foreground/70">Improve:</span> {IMPROVEMENT_TEMPLATES[dimensionKey]}</p>
        </div>
      )}
    </div>
  );
}

export function FluencyStrengths({ dimensionAvgs, scoredCount }: FluencyStrengthsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Show empty state if user has < 3 scored submissions
  if (scoredCount !== undefined && scoredCount < 3) {
    return (
      <Card className="bg-card/80 backdrop-blur-sm border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            Fluency Strengths
          </CardTitle>
          <CardDescription>Unlock your personalized insights</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">
            <p className="text-sm">Submit {3 - scoredCount} more scored prompt{3 - scoredCount !== 1 ? 's' : ''} to unlock Fluency Strengths.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!dimensionAvgs || Object.keys(dimensionAvgs).length === 0) {
    return null;
  }

  // Calculate beginner categories
  const categories = aggregateDimensionsToCategories(dimensionAvgs);

  // Calculate percent of max for ranking (for advanced view)
  const ranked: RankedDimension[] = DIMENSIONS.map(d => ({
    ...d,
    avgRaw: dimensionAvgs[d.key] || 0,
    percentOfMax: (dimensionAvgs[d.key] || 0) / d.max,
  })).sort((a, b) => b.percentOfMax - a.percentOfMax);

  return (
    <Card className="bg-card/80 backdrop-blur-sm border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          Fluency Strengths
        </CardTitle>
        <CardDescription>Based on your last 10 submissions</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!showAdvanced ? (
          /* Beginner View: 4 category bars */
          <div className="space-y-4">
            {categories.map(cat => (
              <CategoryBar key={cat.key} category={cat} />
            ))}
          </div>
        ) : (
          /* Advanced View: All 10 dimensions sorted by percent of max */
          <div className="space-y-3">
            {ranked.map((d, i) => (
              <DimensionBar 
                key={d.key} 
                label={d.label}
                avgRaw={d.avgRaw}
                max={d.max}
                percentOfMax={d.percentOfMax}
                isStrength={i < 3}
                dimensionKey={d.key as DimensionKey}
                showExplanation={true}
              />
            ))}
          </div>
        )}
        
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full text-muted-foreground hover:text-foreground"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Hide Advanced Metrics
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Advanced Metrics
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
